"use client";

import { useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Brain, Pause, Play, Square, Plus, RotateCcw, ChevronDown, Coffee, Hourglass, AlarmClock } from "lucide-react";
import {
  pauseFocus,
  resumeFocus,
  endFocus,
  resetFocus,
  extendCurrentPhase,
  startBreak,
} from "@/lib/focus-timer-store";
import { useFocusState, getRemainingMs, formatClock } from "@/components/focus/useFocusState";

const ACCENT = "#3B5BDB";
const R = 26;
const CIRC = 2 * Math.PI * R;

type Phase = "normal" | "warning" | "danger";

const PHASE_META: Record<Phase, { color: string }> = {
  normal: { color: "#3B5BDB" }, // niebieski — spokojnie, dużo czasu
  warning: { color: "#F59E0B" }, // żółty — zbliża się przerwa (≤5 min)
  danger: { color: "#EF4444" }, // czerwony — ostatnia minuta / koniec
};

// Kolor i pilność zależą od czasu pozostałego do przerwy.
function getPhase(remaining: number, atEnd: boolean): Phase {
  if (atEnd || remaining <= 60_000) return "danger";
  if (remaining <= 5 * 60_000) return "warning";
  return "normal";
}

// Animowana ikona odliczania: klepsydra „przesypuje się" (obrót 180°) coraz
// szybciej w miarę upływu czasu, a w ostatniej minucie zmienia się w dzwoniący
// budzik. `active` = czy animować (fałsz przy pauzie / reduced-motion).
function CountdownIcon({ phase, active, size }: { phase: Phase; active: boolean; size: number }) {
  const color = PHASE_META[phase].color;
  if (phase === "danger") {
    return (
      <motion.span
        style={{ color, display: "inline-flex" }}
        animate={active ? { rotate: [0, -13, 13, -13, 13, -7, 7, 0] } : { rotate: 0 }}
        transition={active ? { duration: 1.2, repeat: Infinity, repeatDelay: 0.5, ease: "easeInOut" } : undefined}
      >
        <AlarmClock size={size} strokeWidth={2.4} />
      </motion.span>
    );
  }
  const cycle = phase === "warning" ? 3 : 5;
  return (
    <motion.span
      style={{ color, display: "inline-flex" }}
      animate={active ? { rotate: [0, 0, 180, 180, 360] } : { rotate: 0 }}
      transition={
        active
          ? { duration: cycle, times: [0, 0.42, 0.5, 0.92, 1], repeat: Infinity, ease: "easeInOut" }
          : undefined
      }
    >
      <Hourglass size={size} strokeWidth={2.2} />
    </motion.span>
  );
}

export default function FocusTimerWidget({ positionClass }: { positionClass: string }) {
  const reduce = useReducedMotion();
  const state = useFocusState();
  const [expanded, setExpanded] = useState(false);
  // Widget dotyczy tylko fazy skupienia (przerwa ma własny pełny ekran).
  // Nie robimy wczesnego `return null` przed AnimatePresence — utrzymujemy stały
  // kształt drzewa (pułapka reconcilera przy animowanych komponentach root).
  const showWidget = state.mode === "focus";
  const remaining = getRemainingMs(state);
  const running = state.status === "running";
  const elapsed = Math.min(state.durationMs, state.durationMs - remaining);
  const progress = state.durationMs > 0 ? elapsed / state.durationMs : 0;
  const atEnd = remaining <= 0;
  const phase = getPhase(remaining, atEnd);
  const color = PHASE_META[phase].color;

  return (
    <div className={`fixed z-[60] ${positionClass}`} style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
      <AnimatePresence mode="popLayout">
        {showWidget && expanded ? (
          <motion.div
            key="panel"
            initial={reduce ? { opacity: 0 } : { opacity: 0, y: 12, scale: 0.96 }}
            animate={reduce ? { opacity: 1 } : { opacity: 1, y: 0, scale: 1 }}
            exit={reduce ? { opacity: 0 } : { opacity: 0, y: 12, scale: 0.96 }}
            transition={{ type: "spring", stiffness: 340, damping: 28 }}
            className="w-[17rem] overflow-hidden rounded-2xl border border-slate-200 bg-white/95 shadow-2xl backdrop-blur-xl dark:border-slate-700 dark:bg-slate-900/95"
          >
            <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2.5 dark:border-slate-800">
              <div className="flex items-center gap-2">
                <Brain size={15} style={{ color: ACCENT }} />
                <span className="text-sm font-bold text-slate-800 dark:text-white">Skupienie</span>
              </div>
              <button
                onClick={() => setExpanded(false)}
                aria-label="Zwiń"
                className="cursor-pointer rounded-lg p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800"
              >
                <ChevronDown size={16} />
              </button>
            </div>

            <div className="flex items-center gap-4 px-4 py-4">
              <Ring progress={progress} running={running && !reduce} color={color}>
                <CountdownIcon phase={phase} active={running && !reduce} size={26} />
              </Ring>
              <div className="min-w-0 flex-1">
                <p
                  className={`text-2xl font-black tabular-nums leading-none ${phase === "normal" ? "text-slate-900 dark:text-white" : ""}`}
                  style={phase === "normal" ? undefined : { color }}
                >
                  {formatClock(remaining)}
                </p>
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  {atEnd ? "Sesja dobiegła końca" : running ? "pozostało do przerwy" : "wstrzymano"}
                </p>
                {state.autoBreaks && !atEnd && (
                  <p className="mt-1.5 inline-flex items-center gap-1 rounded-md bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                    <Coffee size={11} /> przerwa {Math.round(state.breakMs / 60000)} min
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 px-4 pb-3">
              {running ? (
                <ActionBtn onClick={pauseFocus} icon={<Pause size={14} />} label="Pauza" />
              ) : atEnd ? (
                <ActionBtn onClick={() => startBreak()} icon={<Coffee size={14} />} label="Przerwa" primary />
              ) : (
                <ActionBtn onClick={resumeFocus} icon={<Play size={14} />} label="Wznów" primary />
              )}
              <ActionBtn onClick={() => extendCurrentPhase(5)} icon={<Plus size={14} />} label="5 min" />
              <ActionBtn onClick={() => startBreak()} icon={<Coffee size={14} />} label="Przerwa" />
              <ActionBtn onClick={endFocus} icon={<Square size={14} />} label="Zakończ" />
            </div>
            <button
              onClick={resetFocus}
              className="flex w-full items-center justify-center gap-1.5 border-t border-slate-100 py-2 text-[11px] font-medium text-slate-400 transition-colors hover:text-slate-600 dark:border-slate-800 dark:hover:text-slate-300"
            >
              <RotateCcw size={11} /> Zresetuj timer
            </button>
          </motion.div>
        ) : showWidget ? (
          <motion.button
            key="pill"
            layout
            onClick={() => setExpanded(true)}
            initial={reduce ? { opacity: 0 } : { opacity: 0, y: 12, scale: 0.9 }}
            animate={reduce ? { opacity: 1 } : { opacity: 1, y: 0, scale: 1 }}
            exit={reduce ? { opacity: 0 } : { opacity: 0, y: 12, scale: 0.9 }}
            transition={{ type: "spring", stiffness: 360, damping: 26 }}
            className="group flex items-center gap-2.5 rounded-full border border-slate-200 bg-white/95 py-2 pl-2 pr-4 shadow-xl backdrop-blur-xl transition-shadow hover:shadow-2xl dark:border-slate-700 dark:bg-slate-900/95"
          >
            <span className="relative flex items-center justify-center">
              <Ring progress={progress} running={running && !reduce} color={color} small>
                <CountdownIcon phase={phase} active={running && !reduce} size={15} />
              </Ring>
            </span>
            <span className="flex flex-col items-start leading-none">
              <span
                className={`text-sm font-black tabular-nums ${phase === "normal" ? "text-slate-900 dark:text-white" : ""}`}
                style={phase === "normal" ? undefined : { color }}
              >
                {formatClock(remaining)}
              </span>
              <span className="mt-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                {atEnd ? "Koniec" : running ? "Skupienie" : "Pauza"}
              </span>
            </span>
          </motion.button>
        ) : null}
      </AnimatePresence>
    </div>
  );
}

function Ring({
  progress,
  running,
  color,
  small,
  children,
}: {
  progress: number;
  running: boolean;
  color: string;
  small?: boolean;
  children: React.ReactNode;
}) {
  const size = small ? 34 : 64;
  const r = small ? 13 : R;
  const circ = 2 * Math.PI * r;
  return (
    <span className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="currentColor" strokeWidth={small ? 3 : 4} className="text-slate-200 dark:text-slate-700" />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={small ? 3 : 4}
          strokeLinecap="round"
          strokeDasharray={small ? 2 * Math.PI * r : CIRC}
          strokeDashoffset={circ * (1 - Math.min(1, Math.max(0, progress)))}
          style={{ transition: "stroke-dashoffset 0.9s linear, stroke 0.5s ease" }}
        />
      </svg>
      {running && (
        <span
          className="absolute inset-0 rounded-full"
          style={{ animation: "focusPulse 2.4s ease-in-out infinite", boxShadow: `0 0 0 0 ${color}55` }}
        />
      )}
      <span className="absolute inset-0 flex items-center justify-center">{children}</span>
      <style jsx>{`
        @keyframes focusPulse {
          0% {
            box-shadow: 0 0 0 0 ${color}44;
          }
          70% {
            box-shadow: 0 0 0 8px ${color}00;
          }
          100% {
            box-shadow: 0 0 0 0 ${color}00;
          }
        }
      `}</style>
    </span>
  );
}

function ActionBtn({
  onClick,
  icon,
  label,
  primary,
}: {
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  primary?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center justify-center gap-1.5 rounded-xl py-2 text-xs font-semibold transition-colors cursor-pointer ${
        primary
          ? "bg-primary text-white hover:bg-primary-dark"
          : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
      }`}
    >
      {icon}
      {label}
    </button>
  );
}
